<script setup lang="ts">
import { from } from '@nuts/auto-delivery-sdk/dist/utils/money';
import { computed, useTemplateRef } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

import { fromAxiosNutsJson, webstore } from '@/api';
import ThemedButton from '@/components/base/ThemedButton.vue';
import QuantitySelector from '@/components/custom-trays/QuantitySelector.vue';
import { useCallback } from '@/composables/useCallback';
import { useCart } from '@/composables/useCart';
import money from '@/filters/money';
import { CustomProductResponse, CustomProductType } from '@/lib/customizers/digitalGifting';
import { MEDIUM_CUSTOM_TRAY_SKU } from '@/lib/customizers/trays';
import { NutsAddToCartItem, sendProductAddedEvent } from '@/utils/analytics/productAddedEvent';
import { getCartId } from '@/utils/cart';
import { CustomTraysProps } from '@/views/CustomTrays.vue';

type Ingredient = CustomTraysProps['ingredientCatalog'][number];
type IngredientPriceField = 'priceLi' | 'priceLo' | 'priceMi' | 'priceMo';
type MaybeIngredient = Ingredient | undefined;

const props = defineProps<{
  digitalGifting: CustomTraysProps['digitalGifting'];
  isDigitalGifting?: boolean;
  skus: CustomTraysProps['skus'];
}>();

const activeIngredientId = defineModel<string>('activeIngredientId');
const activeSlot = defineModel<number>('activeSlot');
const quantity = defineModel<number>('quantity', { required: true });
const sku = defineModel<string>('sku', { required: true });
const trayContents = defineModel<MaybeIngredient[]>('trayContents', { required: true });

const selectedSku = computed(
  () => props.skus.find((e) => e.externalId === sku.value) ?? props.skus[0],
);
const ingredientAtSlot = (slot: number) => trayContents.value[slot - 1];

function calculateTraySectionPrice(traySize: 'L' | 'M', slot: number, ingredient: MaybeIngredient) {
  const outerOrInner = slot === 4 ? 'i' : 'o';
  const field: IngredientPriceField = `price${traySize}${outerOrInner}`;
  return (ingredient && ingredient[field]) || 0;
}

function calculateTrayPrice(traySize: 'L' | 'M') {
  if (trayContents.value.filter(Boolean).length === 0) {
    return 0;
  }

  const basePrice = traySize === 'L' ? 20 : 20;
  const ingredientPrice = trayContents.value
    .map((ingredient, i) => calculateTraySectionPrice(traySize, i, ingredient))
    .reduce((acc, value) => acc + value, 0);
  return basePrice + ingredientPrice;
}

const currentMediumTrayPrice = computed<number>(() => calculateTrayPrice('M'));
const currentLargeTrayPrice = computed<number>(() => calculateTrayPrice('L'));
const totalPrice = computed(() =>
  sku.value === props.skus[1].externalId
    ? currentLargeTrayPrice.value
    : currentMediumTrayPrice.value,
);

const formattedMediumTrayPrice = computed(() => money(currentMediumTrayPrice.value));
const formattedLargeTrayPrice = computed(() => money(currentLargeTrayPrice.value));
const formattedTotalPrice = computed(() => money(totalPrice.value * quantity.value));

const onDrop = (e: DragEvent, slot: number) => {
  const ingredientId = e.dataTransfer?.getData('ingredientId');
  if (ingredientId) {
    activeIngredientId.value = ingredientId;
    activeSlot.value = slot;
  }
};
const updateSlot = (position: number) => {
  if (!activeIngredientId.value && ingredientAtSlot(position)) {
    trayContents.value[position - 1] = undefined;
  } else {
    activeSlot.value = position;
  }
};

const store = useStore();
const { cart, setCart } = useCart(store);
const form = useTemplateRef('form');
const router = useRouter();

function persistDigitalGiftedProduct() {
  const digitalGiftedProduct = {
    ingredients: trayContents.value,
    giftCertificateCode: props.digitalGifting?.giftCertificateCode,
    giftedSku: props.digitalGifting?.digitalGiftSku,
    size: props.digitalGifting?.giftedTray.sku,
    type: CustomProductType.Tray,
    giftMessage: props.digitalGifting?.giftMessage,
    senderName: props.digitalGifting?.senderName,
  };
  sessionStorage.setItem('digitalGiftedProduct', JSON.stringify(digitalGiftedProduct));
}

const createCustomTray = useCallback(async () => {
  const formData = new FormData(form.value!);
  await fromAxiosNutsJson<CustomProductResponse>(webstore.post('/custom-trays/create', formData), {
    onRedirect: async (url, _, data) => {
      if (data) setCart(data.cart);
      await router.push(url);
    },
  });
});

function buildProductAddedPayload(): NutsAddToCartItem {
  return {
    cartId: getCartId(cart.value),
    markedAsGift: false,
    name: selectedSku.value.name,
    pageSection: 'Buy Block',
    price: from(totalPrice.value),
    priceBeforeDiscount: from(totalPrice.value),
    productId: selectedSku.value.itemKey,
    quantity: quantity.value,
    reportingCategory: 'Gifts > Custom Tray > Cust. Tray Custom Tray',
    sku: selectedSku.value.externalId,
    weight: sku.value === MEDIUM_CUSTOM_TRAY_SKU ? 4 : 6,
  };
}

const addToCartAction = async () => {
  if (props.isDigitalGifting && props.digitalGifting) {
    persistDigitalGiftedProduct();

    router.push('/gift-drop/redeem/address');
  } else {
    const payload = buildProductAddedPayload();

    sendProductAddedEvent(payload);

    await createCustomTray.execute();
  }
};

const styleForSlot = (slot: number) => {
  const ingredient = trayContents.value.find((_, zeroBasedSlot) => zeroBasedSlot === slot - 1);
  if (!ingredient) return undefined;

  return {
    backgroundImage: `url(/images/items/custom/0${ingredient.id}_0${[0, 1, 2, 4, 5, 3][slot]}.jpg)`,
  };
};
</script>

<template>
  <form
    class="form-horizontal builder-buy-block buy-block"
    @submit.prevent="addToCartAction"
    data-test="buy-block"
    ref="form"
  >
    <div class="row">
      <div class="col-xs-12 col-md-12">
        <div id="tray" data-test="tray-image">
          <div
            v-for="position in [1, 2, 4, 5, 3]"
            :key="`image_${position}`"
            class="slot"
            data-test="tray-slot"
            :class="[`position-${position}`, { filled: ingredientAtSlot(position) }]"
            :style="styleForSlot(position)"
            :data-position="`position-${position}`"
            @click.prevent="updateSlot(position)"
            @drop="onDrop($event, position)"
            @dragover.prevent
            @dragenter.prevent
          >
            <a href="#" class="hidden-lg">+</a>
          </div>
        </div>
      </div>
    </div>

    <div v-if="isDigitalGifting">
      <ThemedButton
        class="mt-8"
        theme="gray"
        :disabled="trayContents.filter(Boolean).length !== 5"
        fullWidth
      >
        Continue to Shipping Address
      </ThemedButton>
    </div>

    <div v-else class="row">
      <fieldset>
        <legend style="text-indent: -3000px">Size</legend>
        <div
          v-for="({ details, externalId, name }, index) in skus"
          :key="externalId"
          class="col-md-12 size-section hidden-sm hidden-xs"
        >
          <div class="row">
            <div class="medium col-md-8" data-test="custom-tray-size">
              <input
                aria-label="Size"
                :id="index"
                :value="externalId"
                v-model="sku"
                type="radio"
                name="size"
              />
              <label :for="`${index}`">
                <strong>{{ name.replace('Custom ', '') }}</strong>
                <div class="weight">{{ details }}</div>
              </label>
            </div>
            <div class="col-md-4 price-container" data-test="custom-tray-price-container">
              <span>
                {{
                  externalId === MEDIUM_CUSTOM_TRAY_SKU
                    ? formattedMediumTrayPrice
                    : formattedLargeTrayPrice
                }}
              </span>
            </div>
          </div>
        </div>
      </fieldset>

      <div class="col-xs-12">
        <div class="row">
          <div class="col-sm-12 size-section hidden-md hidden-lg">
            <h6>Size</h6>
            <select
              aria-label="Size"
              v-model="sku"
              name="size"
              id="size-selector"
              data-test="size-selector-mobile"
              class="arrow-indicator"
            >
              <option v-for="{ externalId, name } in skus" :key="externalId" :value="externalId">
                {{ name.replace('Custom ', '') }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <div class="col-xs-12 quantity-total-container">
        <div class="row">
          <div class="col-xs-12 col-md-8">
            <QuantitySelector v-model="quantity" class="quantity-selector" />
          </div>
          <div class="col-md-4 hidden-xs total-container" data-test="total-cost">
            <span>{{ formattedTotalPrice }}</span>
          </div>
        </div>
      </div>

      <div class="col-xs-12">
        <div
          v-if="selectedSku.isPurchaseAllowed"
          class="add-to-cart-button-box add-to-cart-sidebar add-to-cart-sticky"
        >
          <div
            class="price-and-quantity-container hidden-md hidden-lg"
            data-test="price-and-quantity-container"
          >
            <h3 class="total">{{ formattedTotalPrice }}</h3>
            <p class="quantity-info">Qty: {{ quantity }}</p>
          </div>
          <div class="w-full button-container">
            <ThemedButton
              class="w-full"
              data-test="add-to-cart-button"
              :disabled="trayContents.filter(Boolean).length !== 5"
              :isLoading="createCustomTray.isPending"
              type="submit"
            >
              Add to Cart
            </ThemedButton>
          </div>
        </div>
        <span v-else class="out-of-stock">We’re sorry, this product is out of stock</span>
      </div>
    </div>
    <input
      type="hidden"
      :value="trayContents.map((e) => e?.id ?? '').join(',')"
      name="tray-contents"
    />
  </form>
</template>

<style lang="scss" scoped>
.button-container {
  display: inline-block;
  vertical-align: top;
  width: 100%;
}
@include respond-max($screen-xs-max) {
  .add-to-cart-button-box {
    display: flex;
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    height: 75px;
    border-top: 1px $alto solid;
    z-index: 40;
    background-color: white;
    padding: 20px;
  }
}

.add-to-cart-sidebar {
  &:deep(button) {
    font-size: 15px;
  }
}
.add-to-cart-sticky {
  .out-of-stock {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    height: 75px;
    border-top: 1px $alto solid;
    z-index: 999999;
    text-align: center;
    background-color: $gray;

    span {
      display: block;
      color: white;
      font-size: 16px;
      line-height: 75px;
    }
  }
}

.price-and-quantity-container {
  display: inline-block;
  width: 0%;
  @include respond-max($screen-md-min) {
    width: 35%;
  }
  h3 {
    color: $limeade;
    font-size: 20px;
  }
  p {
    color: $zambezi;
    font-size: 14px;
    line-height: 14px;
    margin-bottom: inherit;
  }
}

.total-container {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
}
</style>
